"use client";

import React from "react";

import Image from "next/image";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { useTransform, type Variants } from "framer-motion";

import Chip from "@/components/bastian-keller/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import PhotoCard from "@/components/bastian-keller/PhotoCard";
import HeroShowreel from "@/components/bastian-keller/HeroShowreel";
import HeroStats from "@/components/bastian-keller/HeroStats";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { MotionDiv } from "@/components/global/utils/motionTags";
import { promoteLayer } from "@/components/global/utils/motionLayer";
import { useMotionMode } from "@/components/global/utils/motionMode";
import { after, EASE_OUT, STACK_STEP } from "@/components/global/utils/motionTiming";
import { useRevealProgress } from "@/components/global/utils/scrollReveal";

import { PageBastianKellerSchema } from "@/sanity/pages/PageBastianKellerTemplate";

const LETTERS = "url(/bastian-keller/wordmark-letters.svg)";

/**
 * The hero plate is the section's background with the wordmark punched out of it,
 * so the fixed backdrop behind shows through the letterforms. Two mask layers —
 * a full-cover fill minus the letters — composited with `exclude`.
 *
 * The fill layer spans the whole plate, which is full-bleed. The letters layer is
 * sized to the centred 240rem design box instead, because that is what the rest of
 * the hero is laid out against — the letters SVG shares the 1920x1098 viewBox, so
 * scaling it to that box lands the type pixel-perfect.
 *
 * Its height is `auto` and it is anchored to the bottom rather than stretched to
 * the plate. The plate is shorter than the SVG's own 1098 now, so a `100%` height
 * would squash the letterforms; sizing off the width alone keeps them undistorted
 * at any viewport, and the bottom anchor is what the design aligns them to.
 */
const LETTERS_SIZE = "min(240rem,100%) auto";

const KNOCKOUT: React.CSSProperties = {
  maskImage: `linear-gradient(#000,#000), ${LETTERS}`,
  maskSize: `100% 100%, ${LETTERS_SIZE}`,
  maskPosition: "center, center bottom",
  maskRepeat: "no-repeat, no-repeat",
  maskComposite: "exclude",
  WebkitMaskImage: `linear-gradient(#000,#000), ${LETTERS}`,
  WebkitMaskSize: `100% 100%, ${LETTERS_SIZE}`,
  WebkitMaskPosition: "center, center bottom",
  WebkitMaskRepeat: "no-repeat, no-repeat",
  WebkitMaskComposite: "xor",
};

/*
  Load sequence — the one part of the page that is not scrubbed off scroll, and
  deliberately so. This content is above the fold at scroll position zero, so
  there is no scroll to drive it with: a scrubbed hero would simply be blank when
  the page opens and would stay blank until the visitor moved. Everything below
  the fold, here and in the other sections, is scroll-linked.

  Every step is capped at 800ms and each begins partway into the one before it,
  so the hero arrives as a single move.

  With motion off the sequence does not play a shortened version of itself: the
  elements below take `initial={false}`, which drops the hidden state entirely
  and renders them where the sequence would have left them. That is the honest
  static reading of a load animation, and it has a second benefit — `hidden` is
  what the server renders, so an opacity-0 hero used to sit there until hydration
  caught up. Off means the markup arrives already visible.
*/
const LINE_DRAW = 0.8;
const PORTRAIT_REVEAL = 0.8;
const COLUMN_RISE = 0.8;
// ambient rather than a step in the sequence: the backdrop is still easing back
// behind the letterforms after everything else has landed
const BACKDROP_SETTLE = 1.8;

const PORTRAIT_START = after(0, LINE_DRAW, 0.25);
const COLUMN_START = after(PORTRAIT_START, PORTRAIT_REVEAL, 0.35);
/*
  The stats flank the portrait opposite the copy column, so they open on the same
  beat rather than queueing behind it — one move arriving from both sides. Their
  own rows then stack on the shared 90ms step, same as the column's three blocks.
*/
const STATS_START = COLUMN_START + STACK_STEP;
// chip, paragraph and signature run the same move on a fixed 90ms step, so all
// three are mid-flight together. Deriving the gap from the rise instead — however
// generous the overlap — spaces them out enough that each visibly waits its turn.
const COLUMN_STEP = STACK_STEP;
/*
  The signature is the copy column's third block on desktop but sits below the
  stats on mobile, so it is a sibling rather than a child — see SIGNATURE_BOTTOM.
  It keeps the beat it had as the column's last child.
*/
const SIGNATURE_START = COLUMN_START + 2 * COLUMN_STEP;
/*
  The wordmark closes the sequence. It runs longer than anything above it because
  it travels further — a full-bleed sweep against moves of a couple of rem — and
  it is the one thing on the plate worth watching arrive, so it is still going as
  the counters opposite settle.

  It is also the one move here that does not ride EASE_OUT, for the same reason
  the counters do not (see EASE_OUT_SETTLED, and `useReveal`'s linear track). That
  curve puts 86% of the distance in the first sixth of the run, which is what you
  want for a 2.5rem rise and wrong for something crossing the whole screen: the
  sweep flicks past in 200ms and then spends a second creeping over the last few
  pixels. A plain cubic ease-out spends the time on the part you can see — measured
  43% through at 200ms against EASE_OUT's 86%.
*/
const WORDMARK_EASE = [0.33, 1, 0.68, 1] as const;
const WORDMARK_SET = 1.2;
const WORDMARK_START = after(COLUMN_START, COLUMN_RISE);

/**
 * The wordmark's geometry, read off the SVG: the letterforms occupy y 922.5 to
 * 1039.8 of the 1098-tall viewBox — 7.28rem to 21.94rem up from its bottom.
 *
 * Up from the bottom is the only measurement that survives a plate whose height
 * is not known until it renders. It is also all the mask needs, being bottom
 * anchored, so the cover matches the letters at any height with nothing to keep
 * in sync. The margin either side covers antialiasing.
 */
const WORDMARK_BOTTOM = 6.25;
const WORDMARK_HEIGHT = 16.5;

/**
 * The plate takes the viewport rather than a frame of its own, so the wordmark
 * sits on the bottom edge of the window at rest. At 1920 the Figma frame put the
 * letterforms at 916-1048px and a maximised browser on a 1080p screen ends around
 * 950, so the word you scroll onto was never on screen.
 *
 * There is deliberately no ceiling. The plate is what hides the fixed backdrop —
 * anything the plate does not cover is video — so capping it at the design's own
 * 137.25rem played the film in a band along the bottom of every window taller
 * than that. The frame is still reproduced exactly at the height it was drawn
 * for; past it the plate keeps going and the portrait takes up the slack.
 *
 * The floor is where the composition stops meaning anything: at 107.5rem the
 * portrait is down to ~50rem, about as wide as the paragraph beside it, and any
 * smaller it stops being the thing the hero is about. Shorter windows scroll —
 * and the plate still covers them, being the taller of the two.
 */
const PLATE_HEIGHT = "max(107.5rem, 100svh)";

/**
 * Portrait width, derived from whatever height the plate ended up with.
 *
 * The card is pinned 12rem below the top of the plate and 27.566rem above its
 * bottom — that lower gap is the room the wordmark needs — so its height is
 * whatever is left over, and the width follows from the 534:687 crop. The two
 * constants are the parts that do not scale with it: 2.047rem of frame border and
 * padding, and 4.114rem of caption plus the gap above it.
 *
 * At a 137.25rem plate this comes out at 73.18rem, the Figma width to the
 * hundredth of a rem — the design falls out of the expression rather than being
 * a case inside it.
 *
 * 96rem is a backstop, not a design value. The photo is centred between two
 * columns of type whose inner edges are 68rem and 178.125rem across, and a window
 * tall enough for a 110rem portrait would have it growing straight through them.
 * That needs a window about 1.4x taller than it is wide; past there the growth
 * stops and the extra height opens up above the wordmark instead.
 */
const PORTRAIT_WIDTH = "min(96rem, calc((var(--plate) - 45.727rem) * 534 / 687 + 2.047rem))";

/**
 * Bottom offsets for the two type blocks flanking the portrait. They are anchored
 * to the bottom of the plate for the same reason the mask is: their relationship
 * is with the portrait's lower edge and the wordmark under it, not with the top of
 * a plate whose height moves. Anchoring by the top instead would have both blocks
 * drift away from the photo as the window grew.
 */
const SIGNATURE_BOTTOM = 31.4;
const SIGNATURE_HEIGHT = 12.75;
const COLUMN_GAP = 2.125;
/*
  The copy column ends where the signature used to begin: the two were one stack
  and are now siblings, so the column clears the signature's own box and the gap
  that separated them.
*/
const COLUMN_BOTTOM = SIGNATURE_BOTTOM + SIGNATURE_HEIGHT + COLUMN_GAP;
const STATS_BOTTOM = 32.7;

/** Drift of the two content blocks as the plate scrolls off, in rem. */
const PORTRAIT_DRIFT = 8;
const COLUMN_DRIFT = 3;

/**
 * The plate's own travel off the top of the screen. Unlike the shared ranges this
 * one starts with the plate already filling the viewport, because it does — this
 * is the top of the page.
 */
const PLATE_OFFSET = ["start start", "end start"];

const backdropVariants: Variants = {
  hidden: { scale: 1.12 },
  shown: {
    scale: 1,
    transition: { duration: BACKDROP_SETTLE, ease: EASE_OUT },
  },
};

/** The guide lines draw downward, which is what opens the sequence. */
const lineVariants: Variants = {
  hidden: { scaleY: 0 },
  shown: { scaleY: 1, transition: { duration: LINE_DRAW, ease: EASE_OUT } },
};

/**
 * Portrait — a mask opens upward while the card rides up behind it at its own
 * rate, so it arrives from under its own edge rather than simply fading up.
 */
const portraitWipeVariants: Variants = {
  hidden: { clipPath: "inset(100% 0% 0% 0%)" },
  shown: {
    clipPath: "inset(0% 0% 0% 0%)",
    transition: {
      duration: PORTRAIT_REVEAL,
      ease: EASE_OUT,
      delay: PORTRAIT_START,
    },
  },
};

const portraitRiseVariants: Variants = {
  hidden: { y: "6rem" },
  shown: {
    y: "0rem",
    transition: {
      duration: PORTRAIT_REVEAL,
      ease: EASE_OUT,
      delay: PORTRAIT_START,
    },
  },
};

/**
 * The cover sitting over the knockout, collapsing to its right edge — so the word
 * is set left to right and reads in the order you would read it.
 *
 * It has no motion-off counterpart because there is nothing to leave behind: with
 * motion off the cover is not rendered at all and the letterforms are simply
 * there. A variant that reset `scaleX` to 1 would do the opposite of that.
 */
const wordmarkVariants: Variants = {
  hidden: { scaleX: 1 },
  shown: {
    scaleX: 0,
    transition: {
      duration: WORDMARK_SET,
      ease: WORDMARK_EASE,
      delay: WORDMARK_START,
    },
  },
};

const columnVariants: Variants = {
  hidden: {},
  shown: {
    transition: { delayChildren: COLUMN_START, staggerChildren: COLUMN_STEP },
  },
};

/**
 * One move for all three blocks in the column — chip, paragraph and signature
 * differ only in when they start. A plain rise also keeps the signature intact:
 * a mask reveal would have had to fight the artwork's deliberate bleed outside
 * its layout box.
 */
const columnItemVariants: Variants = {
  hidden: { opacity: 0, y: "2.5rem" },
  shown: {
    opacity: 1,
    y: "0rem",
    transition: { duration: COLUMN_RISE, ease: EASE_OUT },
  },
};

/** The column's move, carrying its own delay now that it has no parent stagger. */
const signatureVariants: Variants = {
  hidden: { opacity: 0, y: "2.5rem" },
  shown: {
    opacity: 1,
    y: "0rem",
    transition: { duration: COLUMN_RISE, ease: EASE_OUT, delay: SIGNATURE_START },
  },
};

/**
 * The motion-off stand-in for every variant above.
 *
 * It has to neutralise each property those variants set, not just fade. The mode
 * is not known at first render — `useIsMobile` reports `null` until its queries
 * resolve, so the hero mounts in its `hidden` state and the swap to this object
 * happens afterwards. framer writes animated values inline and does not clear a
 * property once a variants object stops mentioning it, so a fallback that only
 * set `opacity` left the portrait sitting at `clipPath: inset(100% 0 0)` — clipped
 * away entirely — with the rise stuck at 6rem underneath it.
 */
const staticVariants: Variants = {
  hidden: { opacity: 0 },
  shown: {
    opacity: 1,
    y: "0rem",
    scale: 1,
    scaleY: 1,
    clipPath: "inset(0% 0% 0% 0%)",
    transition: { duration: 0.5, ease: EASE_OUT },
  },
};

/**
 * The guides' own motion-off stand-in. They cannot share `staticVariants`: its
 * `clipPath` reset — there to undo the portrait wipe — lands on wrappers that
 * are zero pixels wide, and `inset(0)` on a zero-width box clips the 2px line
 * hanging off it out of existence. That is what made the guides vanish below
 * lg, where the page runs with motion off. Only what `lineVariants` sets needs
 * neutralising here.
 */
const staticLineVariants: Variants = {
  hidden: { opacity: 0 },
  shown: {
    opacity: 1,
    scaleY: 1,
    transition: { duration: 0.5, ease: EASE_OUT },
  },
};

export default function HeroSection({ data, className }: { data: PageBastianKellerSchema; className?: string }) {
  const sectionRef = React.useRef<HTMLElement>(null);
  const plateRef = React.useRef<HTMLDivElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const hasVideo = Boolean(data?.heroVideo?.muxVideo?.playbackId);

  // below lg the hero runs in flow rather than pinned to the Figma frame, so the
  // two blocks have no fixed relationship to drift against — and below lg the
  // page does not animate at all
  const { reduceMotion, parallax } = useMotionMode();

  // progress across the plate alone rather than the whole section: the content is
  // only on screen while the plate is, and the reveal band below it is empty
  const progress = useRevealProgress(plateRef, PLATE_OFFSET, !parallax);

  const portraitY = useTransform(progress, [0, 1], ["0rem", `${parallax ? -PORTRAIT_DRIFT : 0}rem`]);
  const columnY = useTransform(progress, [0, 1], ["0rem", `${parallax ? -COLUMN_DRIFT : 0}rem`]);

  // the backdrop is fixed, so it keeps decoding long after it is covered —
  // park it whenever the hero and its reveal band are off screen
  React.useEffect(() => {
    const section = sectionRef.current;
    if (!section || !hasVideo) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const video = videoRef.current;
        if (!video) return;
        if (entry.isIntersecting) {
          void video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { rootMargin: "10% 0px" },
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [hasVideo]);

  return (
    <section
      ref={sectionRef}
      className={cn("relative", className)}>
      {/*
        Fixed backdrop. Because it does not scroll, the hero plate travelling up
        the screen uncovers it from the bottom — which is the reveal. Sections
        further down are opaque and painted later, so they cover it again.
      */}
      <div className="pointer-events-none fixed inset-0 z-0 bg-[#0A1A1D]">
        {/*
          Held slightly over-scale and eased back, so whatever shows through the
          wordmark is still moving while the rest of the hero settles.
        */}
        <MotionDiv
          className="absolute inset-0"
          initial={reduceMotion ? false : "hidden"}
          animate="shown"
          variants={reduceMotion ? staticVariants : backdropVariants}>
          {hasVideo ? (
            <HLSVideoPlayer
              ref={videoRef}
              source={data.heroVideo}
              autoPlay
              muted
              loop
              playsInline
              className="absolute top-0 left-0 !h-full !w-full object-cover"
            />
          ) : (
            data?.heroVideoPlaceholder?.src && (
              <Image
                className="absolute top-0 left-0 !h-full !w-full object-cover"
                src={data.heroVideoPlaceholder}
                alt=""
                priority
                quality={100}
              />
            )
          )}
        </MotionDiv>
      </div>

      {/*
        The plate is full-bleed on purpose. Past 1920px the root font-size switches
        to 800vw/2200, so a 240rem box stops filling the screen — capping the plate
        there would let the fixed backdrop leak down both gutters.

        Its height is the window's, and the portrait's size follows from it — see
        PLATE_HEIGHT. The two are declared here as custom properties rather than
        measured in an effect: the whole layout is one expression of the viewport,
        so the browser can resolve it on the first paint and keep resolving it as
        the window is dragged, with nothing to subscribe to and no frame where the
        hero is the wrong size.
      */}
      <div
        ref={plateRef}
        style={
          {
            "--plate": PLATE_HEIGHT,
            "--portrait": PORTRAIT_WIDTH,
            "--signature-bottom": `${SIGNATURE_BOTTOM}rem`,
          } as React.CSSProperties
        }
        className="relative z-[1] lg:h-[var(--plate)]">
        {/* solid plate below lg, where the flow layout breaks the fixed geometry */}
        <div className="absolute inset-0 bg-[#F3F2F1] lg:hidden" />
        <div
          className="absolute inset-0 hidden bg-[#F3F2F1] lg:block"
          style={KNOCKOUT}
        />

        {/*
          Sits between the plate and the content in paint order, so it hides the
          knockout without ever covering the portrait or the paragraph.

          It is the last step of the load sequence rather than a scrubbed reveal.
          It used to be scrubbed, and could be, because the letterforms sat below
          the fold and there was scroll to drive them with. On a plate that ends
          at the bottom of the window they are on screen at rest, so a scrubbed
          wipe would either be finished before you touched the wheel or hold the
          word half-set under a hero that has otherwise finished arriving.
        */}
        {!reduceMotion && (
          <MotionDiv
            aria-hidden
            initial="hidden"
            animate="shown"
            variants={wordmarkVariants}
            /*
              Promoted: a full-bleed bar being scaled every frame of the sweep, and
              a flat fill with no type on it — the layer is cheap and there is no
              antialiasing to lose.
            */
            transformTemplate={promoteLayer}
            className="absolute inset-x-0 hidden origin-right bg-[#F3F2F1] lg:block"
            style={{
              bottom: `${WORDMARK_BOTTOM}rem`,
              height: `${WORDMARK_HEIGHT}rem`,
            }}
          />
        )}

        {/*
          240rem is the 1920px Figma frame: the root font-size tracks the viewport,
          so laying the content out in rem inside this container reproduces the
          design one-to-one at every desktop width.
        */}
        <div className="relative mx-auto w-[min(240rem,100%)] overflow-clip lg:h-full">
          {/* wrapped so the guides can be drawn downward rather than just appearing */}
          <MotionDiv
            initial={reduceMotion ? false : "hidden"}
            animate="shown"
            variants={reduceMotion ? staticLineVariants : lineVariants}
            className="absolute top-[7.25rem] bottom-[2rem] left-[1rem] origin-top lg:top-[6.875rem] lg:bottom-[4.25rem] lg:left-[7.125rem]">
            <DecorationLine
              className="top-0 left-0 h-full"
              orientation="vertical"
              style="dark"
            />
          </MotionDiv>
          <MotionDiv
            initial={reduceMotion ? false : "hidden"}
            animate="shown"
            variants={reduceMotion ? staticLineVariants : lineVariants}
            className="absolute top-[7.25rem] right-[1rem] bottom-[2rem] origin-top lg:top-[6.875rem] lg:right-[7rem] lg:bottom-[4.25rem]">
            <DecorationLine
              className="top-0 right-0 h-full"
              orientation="vertical"
              style="dark"
            />
          </MotionDiv>

          {/* stacks in flow below lg, absolute against the Figma frame from lg up */}
          <div className="relative z-[1] flex flex-col items-start gap-[2rem] px-[2.25rem] pt-[9.625rem] pb-[9rem] lg:block lg:h-full lg:p-0">
            {/*
              The positioning wrapper stays a plain div: it centres the portrait
              with -translate-x-1/2, and an animated transform on the same element
              would overwrite that.
            */}
            {/* the extra 0.5rem makes the design's 2.5rem below the portrait */}
            <div className="mb-[0.5rem] w-full lg:absolute lg:top-[12rem] lg:left-1/2 lg:mb-0 lg:w-[var(--portrait)] lg:-translate-x-1/2">
              <MotionDiv
                style={{ y: portraitY }}
                /*
                  Promoted: the portrait drifts the entire time the plate is on
                  screen, and it is the largest photo above the fold. The copy
                  column beside it deliberately isn't — it drifts 3rem against
                  this one's 8rem, and it is all type.
                */
                transformTemplate={parallax ? promoteLayer : undefined}
                initial={reduceMotion ? false : "hidden"}
                animate="shown"
                variants={reduceMotion ? staticVariants : portraitWipeVariants}>
                <MotionDiv
                  initial={reduceMotion ? false : "hidden"}
                  animate="shown"
                  variants={reduceMotion ? staticVariants : portraitRiseVariants}>
                  <PhotoCard
                    className="w-full"
                    captionClassName="text-[1.5rem] px-[1rem] py-[0.75rem] lg:text-[1.75rem] lg:px-[0.63rem] lg:py-[0.42rem] "
                    /* mobile crops the portrait to a near-square frame */
                    aspect="aspect-[384/351] lg:aspect-[534/687]"
                    image={data?.heroPortrait}
                    caption={data?.heroPortraitCaption}
                    priority
                  />
                </MotionDiv>
              </MotionDiv>
            </div>

            <MotionDiv
              /* `bottom` is inert below lg, where this block is in flow */
              style={{ y: columnY, bottom: `${COLUMN_BOTTOM}rem` }}
              initial={reduceMotion ? false : "hidden"}
              animate="shown"
              variants={columnVariants}
              className="flex w-full flex-col items-start gap-[1.75rem] lg:absolute lg:left-[14.125rem] lg:w-[53.875rem] lg:gap-[2.125rem]">
              {data?.heroChip?.text && (
                <MotionDiv variants={reduceMotion ? staticVariants : columnItemVariants}>
                  <Chip
                    image={data?.heroChip?.icon}
                    text={data?.heroChip?.text}
                  />
                </MotionDiv>
              )}

              {data?.heroText && (
                <MotionDiv
                  variants={reduceMotion ? staticVariants : columnItemVariants}
                  className="richtext richtext-start w-full lg:w-[44.75rem] prose-p:font-geist prose-p:text-[2.75rem] prose-p:leading-[1.1] prose-p:font-medium prose-p:text-black lg:prose-p:leading-[1.26] prose-em:text-black/50 prose-em:not-italic">
                  <PortableText
                    value={data.heroText}
                    components={richTextComponent}
                  />
                </MotionDiv>
              )}
            </MotionDiv>

            {/*
              Stats — opposite the copy column, their last row's edge on the same
              line as the bottom of the photograph. They drift with the same 3rem
              the copy does: both are type flanking the portrait, so separating
              them would shear the row apart as it scrolls.
            */}
            <MotionDiv
              style={{ y: columnY, bottom: `${STATS_BOTTOM}rem` }}
              className="w-full lg:absolute lg:left-[178.125rem] lg:w-[50.625rem]">
              <HeroStats
                stats={data?.heroStats}
                start={STATS_START}
                reduceMotion={reduceMotion}
              />
            </MotionDiv>

            {/*
              Signature — the copy column's last block on desktop, where it is
              absolutely placed to sit under the paragraph, and the last block on
              the page below lg, where the design puts it after the stats. Being a
              sibling rather than a child of the column is what lets one DOM order
              serve both.
            */}
            {data?.heroSignature?.src && (
              <MotionDiv
                /*
                  The offset is a class reading --signature-bottom rather than an
                  inline `bottom`, because this block stays `relative` below lg — for
                  the artwork inside it — so an inline offset would apply there too
                  and lift it off the stack.
                */
                style={{ y: columnY }}
                initial={reduceMotion ? false : "hidden"}
                animate="shown"
                variants={reduceMotion ? staticVariants : signatureVariants}
                /*
                  The signature artwork deliberately bleeds outside its layout box
                  in the design, overlapping the paragraph above it — hence the
                  negative offsets and the fixed intrinsic width.
                */
                className="relative h-[12.75rem] w-[22.25rem] lg:absolute lg:bottom-[var(--signature-bottom)] lg:left-[14.125rem]">
                <Image
                  className="absolute -top-[3.81rem] -left-[1.04rem] !w-[30.7rem] max-w-none"
                  src={data.heroSignature}
                  alt=""
                  quality={100}
                />
              </MotionDiv>
            )}
          </div>
        </div>

        {/*
          The divider runs edge to edge in the design, so it sits on the full-bleed
          plate — but not on its edge. Both frames keep a strip of plate below it
          before the showreel: 16px on mobile, 34px on desktop, where it sits 3rem
          under the knocked-out letterforms. The rule is 2px tall and positioned by
          its bottom edge, so it sits 2px under the guides' end: they stop on its
          top edge, as in the design.
        */}
        <DecorationLine
          className="bottom-[1.75rem] lg:bottom-[4rem]"
          orientation="horizontal"
          style="dark"
        />
      </div>

      {/*
        Reveal band — no background, so the fixed backdrop is fully visible here.
        It is also the only place the video reads as a video rather than as fill
        inside the letterforms, so it doubles as the hit area for its sound.
      */}
      {hasVideo ? (
        <HeroShowreel
          className="relative h-[60vh] lg:h-screen"
          videoRef={videoRef}
        />
      ) : (
        <div
          aria-hidden="true"
          className="relative h-[60vh] lg:h-screen"
        />
      )}
    </section>
  );
}
